import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, TrendingUp, TrendingDown, Battery, Zap, Wind, Sun, DollarSign, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function PortfolioOverview() {
  const { t, i18n } = useTranslation();

  const [selectedPeriod, setSelectedPeriod] = useState('30days');

  // Mock data - w prawdziwej aplikacji to by przyszło z API
  const portfolioStats = {
    totalRevenue: 14200000,
    revenueChange: 12.5,
    revenueTarget: 'vs target',
    installedCapacity: 100.0,
    capacityUnit: 'MW',
    assetsCount: 3,
    activeAssets: 2,
    devAssets: 1,
    bessAvailability: 98.4,
    availabilityChange: -0.2,
    availabilityPeriod: 'vs last month',
    avgCapturePrice: 542,
    priceChange: 15,
    priceComparison: 'vs Base Load'
  };

  const revenueData = [
    { month: 'Jan', arbitrage: 400, ancillary: 360, capacityMkt: 100 },
    { month: 'Feb', arbitrage: 450, ancillary: 330, capacityMkt: 100 },
    { month: 'Mar', arbitrage: 500, ancillary: 350, capacityMkt: 100 },
    { month: 'Apr', arbitrage: 520, ancillary: 330, capacityMkt: 100 },
    { month: 'May', arbitrage: 550, ancillary: 280, capacityMkt: 100 },
    { month: 'Jun', arbitrage: 600, ancillary: 300, capacityMkt: 100 },
    { month: 'Jul', arbitrage: 650, ancillary: 280, capacityMkt: 100 },
    { month: 'Aug', arbitrage: 630, ancillary: 270, capacityMkt: 100 },
    { month: 'Sep', arbitrage: 620, ancillary: 320, capacityMkt: 100 },
    { month: 'Oct', arbitrage: 580, ancillary: 330, capacityMkt: 100 },
    { month: 'Nov', arbitrage: 560, ancillary: 340, capacityMkt: 100 },
    { month: 'Dec', arbitrage: 600, ancillary: 350, capacityMkt: 100 },
  ];

  const assets = [
    {
      id: 1,
      name: 'Gdańsk Storage 1',
      type: 'BESS',
      capacity: '20MW / 80MWh',
      status: 'online',
      icon: Battery,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      metric: 'SoC',
      metricValue: '45%'
    },
    {
      id: 2,
      name: 'Katowice Solar',
      type: 'PV',
      capacity: '50MW',
      status: 'online',
      icon: Sun,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      metric: 'Gen',
      metricValue: '32 MW'
    },
    {
      id: 3,
      name: 'Poznan Wind',
      type: 'Wind',
      capacity: '30MW',
      status: 'dev',
      icon: Wind,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      metric: 'COD',
      metricValue: 'Q4 2025'
    }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value / 1000000);
  };

  const formatNumber = (value, decimals = 1) => {
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + entry.value, 0);
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 mb-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-xs text-gray-600">{entry.name}</span>
              </div>
              <span className="text-xs font-medium text-gray-900">{formatNumber(entry.value)}k</span>
            </div>
          ))}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">{t('portfolioOverview.chart.total')}</span>
              <span className="text-xs font-bold text-gray-900">{formatNumber(total)}k</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('portfolioOverview.title')}</h1>
        <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
          {t('portfolioOverview.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {/* Empty space for alignment */}
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7days">{t('portfolioOverview.periods.last7Days')}</option>
            <option value="30days">{t('portfolioOverview.periods.last30Days')}</option>
            <option value="90days">{t('portfolioOverview.periods.last90Days')}</option>
            <option value="year">{t('portfolioOverview.periods.thisYear')}</option>
          </select>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            <Filter className="w-4 h-4" />
            <span>{t('portfolioOverview.filter')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {t('portfolioOverview.stats.totalRevenue')}
              </p>
              <p className="text-xs text-gray-500 mt-1">(YTD)</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">
              {formatCurrency(portfolioStats.totalRevenue)}M
            </span>
            <span className="text-sm text-gray-600">PLN</span>
          </div>
          <div className="flex items-center space-x-1 text-sm">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-green-600 font-medium">+{portfolioStats.revenueChange}%</span>
            <span className="text-gray-500">{portfolioStats.revenueTarget}</span>
          </div>
        </div>

        {/* Installed Capacity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {t('portfolioOverview.stats.installedCapacity')}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Battery className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">
              {formatNumber(portfolioStats.installedCapacity, 1)}
            </span>
            <span className="text-sm text-gray-600">{portfolioStats.capacityUnit}</span>
          </div>
          <div className="text-sm text-gray-600">
            {portfolioStats.assetsCount} {t('portfolioOverview.stats.assets')} •{' '}
            <span className="text-green-600 font-medium">{portfolioStats.activeAssets} {t('portfolioOverview.stats.active')}</span>,{' '}
            <span className="text-gray-500">{portfolioStats.devAssets} {t('portfolioOverview.stats.dev')}</span>
          </div>
        </div>

        {/* BESS Availability */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {t('portfolioOverview.stats.bessAvailability')}
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">
              {formatNumber(portfolioStats.bessAvailability, 1)}%
            </span>
          </div>
          <div className="flex items-center space-x-1 text-sm">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <span className="text-red-600 font-medium">{portfolioStats.availabilityChange}%</span>
            <span className="text-gray-500">{portfolioStats.availabilityPeriod}</span>
          </div>
        </div>

        {/* Avg Capture Price */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {t('portfolioOverview.stats.avgCapturePrice')}
              </p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">
              {portfolioStats.avgCapturePrice}
            </span>
            <span className="text-sm text-gray-600">PLN/MWh</span>
          </div>
          <div className="flex items-center space-x-1 text-sm">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-green-600 font-medium">+{portfolioStats.priceChange} PLN</span>
            <span className="text-gray-500">{portfolioStats.priceComparison}</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Composition Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {t('portfolioOverview.chart.title')}
            </h2>
            <div className="flex items-center space-x-4 mt-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                <span className="text-sm text-gray-600">{t('portfolioOverview.chart.arbitrage')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-green-600"></div>
                <span className="text-sm text-gray-600">{t('portfolioOverview.chart.ancillary')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span className="text-sm text-gray-600">{t('portfolioOverview.chart.capacityMkt')}</span>
              </div>
            </div>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  tickFormatter={(value) => `${value}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="capacityMkt" 
                  stackId="a" 
                  fill="#9ca3af" 
                  radius={[0, 0, 0, 0]}
                  name={t('portfolioOverview.chart.capacityMkt')}
                />
                <Bar 
                  dataKey="ancillary" 
                  stackId="a" 
                  fill="#10b981" 
                  radius={[0, 0, 0, 0]}
                  name={t('portfolioOverview.chart.ancillary')}
                />
                <Bar 
                  dataKey="arbitrage" 
                  stackId="a" 
                  fill="#3b82f6" 
                  radius={[4, 4, 0, 0]}
                  name={t('portfolioOverview.chart.arbitrage')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Assets Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('portfolioOverview.assets.title')}
            </h2>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              {t('portfolioOverview.assets.viewAll')}
            </button>
          </div>

          <div className="space-y-2">
            {assets.map((asset) => {
              const Icon = asset.icon;
              return (
                <div key={asset.id} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={`p-2 ${asset.iconBg} rounded-lg flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${asset.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-0.5">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {asset.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ml-2 ${
                        asset.status === 'online' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {asset.status === 'online' 
                          ? t('portfolioOverview.assets.status.online')
                          : t('portfolioOverview.assets.status.inDev')
                        }
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      {asset.capacity} • {asset.type}
                    </p>
                    <div className="flex items-center justify-end space-x-1 text-xs">
                      <span className="text-gray-600">{asset.metric}:</span>
                      <span className="font-medium text-gray-900">{asset.metricValue}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioOverview;

